/**
 * Aba "Carrinhos abandonados" / "Expirados": quem esta no funil na janela.
 *
 * Tabela propria em vez do `LeadsTable` compartilhado porque as colunas que
 * importam aqui sao OUTRAS: em que toque a pessoa esta, qual foi o destino
 * dela na regua (pagou, respondeu, saiu, fim sem voltar) e por qual canal
 * voltou. Mesmas regras de privacidade do painel: nome abreviado, telefone
 * mascarado. Abaixo de `md`, cartoes.
 *
 * O filtro de estado e CONTROLADO pela pagina: "O que fazer agora" abre esta
 * aba ja recortada ("com o comercial", "na regua"). Busca (nome, oferta ou
 * final do telefone) e ordenacao (recentes, antigos, maior/menor valor) sao
 * locais e zeram a paginacao ao mudar. Pronta para milhares de leads: nada
 * renderiza fora da pagina atual.
 */

import { useEffect, useState } from 'react';
import { EmptyState } from '../../../components/PageState';
import { Pagination } from '../../../components/Pagination';
import { Badge, Button, FilterChips, Icon, Input, SectionCard, Select, type ChipOption } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatDateTime, maskPhoneDisplay, PROVIDER_LABEL, shortenName, VALOR_AUSENTE } from '../../../lib/format';
import { TUDO } from '../agregacao';
import type { FunnelKind, FunnelLead, FunnelLeadStatus, TouchDefinition } from '../types';
import { CANAL, STATUS_LEAD, VOCABULARIO, rotuloDoAtraso } from '../vocabulario';

const POR_PAGINA = 10;

const ORDEM_STATUS: readonly FunnelLeadStatus[] = ['na_regua', 'pagou', 'vendedora_fechou', 'com_comercial', 'descadastrou', 'sem_voltar', 'voltou_sozinho'];

type Ordenacao = 'recentes' | 'antigos' | 'maior_valor' | 'menor_valor';

const ORDENACAO_LABEL: Readonly<Record<Ordenacao, string>> = {
  recentes: 'Mais recentes',
  antigos: 'Mais antigos',
  maior_valor: 'Maior valor',
  menor_valor: 'Menor valor',
};

function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function ordenarLeads(leads: readonly FunnelLead[], ordem: Ordenacao): readonly FunnelLead[] {
  const copia = [...leads];
  switch (ordem) {
    case 'recentes':
      return copia.sort((a, b) => b.enteredAt.localeCompare(a.enteredAt));
    case 'antigos':
      return copia.sort((a, b) => a.enteredAt.localeCompare(b.enteredAt));
    case 'maior_valor':
      return copia.sort((a, b) => b.amountCents - a.amountCents || b.enteredAt.localeCompare(a.enteredAt));
    case 'menor_valor':
      return copia.sort((a, b) => a.amountCents - b.amountCents || b.enteredAt.localeCompare(a.enteredAt));
  }
}

function UltimoToque({ kind, lead, touches }: { readonly kind: FunnelKind; readonly lead: FunnelLead; readonly touches: readonly TouchDefinition[] }): JSX.Element {
  if (lead.status === 'voltou_sozinho') {
    return <span className="text-xs text-ink-subtle">antes do 1º toque</span>;
  }
  const toque = touches[lead.lastTouchOrder - 1];
  if (toque === undefined) {
    return <span className="text-xs text-ink-subtle">esperando o 1º toque</span>;
  }
  const canal = toque.channel === 'voice' ? CANAL.voice : CANAL.whatsapp;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Badge tone="accent">{rotuloDoAtraso(kind, toque.delayMinutes)}</Badge>
      <span className="inline-flex items-center gap-1 text-xs text-ink-muted">
        <Icon name={canal.icon} size="xs" />
        {canal.label}
      </span>
    </span>
  );
}

function Estado({ lead }: { readonly lead: FunnelLead }): JSX.Element {
  const s = STATUS_LEAD[lead.status];
  return (
    <Badge tone={s.tone} dot>
      {s.label}
    </Badge>
  );
}

function Voltou({ lead }: { readonly lead: FunnelLead }): JSX.Element {
  if (lead.recoveredAt === null) {
    return <span className="text-ink-subtle">{VALOR_AUSENTE}</span>;
  }
  const via = lead.recoveredVia === null ? 'sozinho' : CANAL[lead.recoveredVia].curto;
  return (
    <span className="flex flex-col">
      <span className="tabular-nums text-ink">{formatDateTime(lead.recoveredAt)}</span>
      <span className="text-xs text-ink-subtle">{via}</span>
    </span>
  );
}

export function TabelaDeLeadsDoFunil({
  kind,
  leads,
  touches,
  estado,
  onEstado,
}: {
  readonly kind: FunnelKind;
  readonly leads: readonly FunnelLead[];
  readonly touches: readonly TouchDefinition[];
  /** `TUDO` ou um `FunnelLeadStatus`. Controlado pela pagina. */
  readonly estado: string;
  readonly onEstado: (estado: string) => void;
}): JSX.Element {
  const v = VOCABULARIO[kind];
  const [offset, setOffset] = useState(0);
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState<Ordenacao>('recentes');

  useEffect(() => {
    setOffset(0);
  }, [estado, busca, ordem, leads]);

  const presentes = ORDEM_STATUS.filter((s) => leads.some((l) => l.status === s));
  const opcoes: ChipOption<string>[] = [
    { value: TUDO, label: 'Tudo', count: formatCount(leads.length) },
    ...presentes.map((s) => ({ value: s, label: STATUS_LEAD[s].label, count: formatCount(leads.filter((l) => l.status === s).length) })),
  ];

  const termo = normalizar(busca.trim());
  const digitos = termo.replace(/\D/g, '');
  const porEstado = estado === TUDO ? leads : leads.filter((l) => l.status === estado);
  const filtrados =
    termo === ''
      ? porEstado
      : porEstado.filter((l) => normalizar(l.name ?? '').includes(termo) || normalizar(l.offerName).includes(termo) || (digitos.length >= 2 && l.phoneE164.endsWith(digitos)));
  const ordenados = ordenarLeads(filtrados, ordem);
  const inicio = Math.min(offset, Math.max(0, ordenados.length - 1));
  const pagina = ordenados.slice(inicio, inicio + POR_PAGINA);
  const somaCents = filtrados.reduce((acc, l) => acc + l.amountCents, 0);

  const filtroAtivo = estado !== TUDO || termo !== '';

  return (
    <SectionCard
      id="leads-do-funil"
      title={v.abaLeads}
      icon={v.abaLeadsIcone}
      description={kind === 'expirados' ? 'Quem teve a assinatura vencida no período e onde cada um está na régua.' : 'Quem abandonou o checkout no período e onde cada um está na régua.'}
      aside={<FilterChips label="Filtrar por estado" prefix="Estado:" options={opcoes} value={estado} onChange={onEstado} tone="accent" />}
      padded={false}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 pb-3">
        <label className="relative block w-full sm:w-72">
          <span className="sr-only">Buscar por nome, {v.colunaOferta.toLowerCase()} ou final do telefone</span>
          <Icon name="busca" size="xs" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
          <Input size="sm" type="search" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder={`Nome, ${v.colunaOferta.toLowerCase()} ou final do telefone…`} className="pl-8" />
        </label>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-xs text-ink-muted">
            <strong className="tabular-nums text-ink">{formatCount(filtrados.length)}</strong> {filtrados.length === 1 ? 'lead' : 'leads'} · {formatBRLFromCents(somaCents)}
          </p>
          <label className="flex items-center gap-2 text-xs text-ink-muted">
            <span className="shrink-0">Ordenar</span>
            <Select size="sm" value={ordem} onChange={(e) => setOrdem((Object.keys(ORDENACAO_LABEL) as Ordenacao[]).find((o) => o === e.target.value) ?? 'recentes')} aria-label="Ordenar leads">
              {(Object.keys(ORDENACAO_LABEL) as Ordenacao[]).map((o) => (
                <option key={o} value={o}>
                  {ORDENACAO_LABEL[o]}
                </option>
              ))}
            </Select>
          </label>
        </div>
      </div>

      {ordenados.length === 0 ? (
        <div className="px-5 pb-5">
          <EmptyState
            title={termo !== '' ? `Nenhum lead com “${busca.trim()}”` : `Nenhum lead ${estado === TUDO ? 'no período' : 'neste estado'}`}
            description={
              termo !== ''
                ? 'A busca olha o nome, a oferta e o final do telefone. Confira a grafia ou limpe a busca para ver todo mundo do estado escolhido.'
                : estado === TUDO
                  ? `Ninguém ${kind === 'expirados' ? 'venceu' : 'abandonou'} nesta janela. Mude o período no cabeçalho ou escolha outro produto.`
                  : `Nenhum lead está em “${STATUS_LEAD[estado as FunnelLeadStatus]?.label ?? estado}” agora. Isso muda conforme a régua anda; volte a “Tudo” para ver todo mundo.`
            }
            action={
              filtroAtivo ? (
                <Button
                  variant="secondary"
                  size="sm"
                  icon="x"
                  onClick={() => {
                    setBusca('');
                    onEstado(TUDO);
                  }}
                >
                  Limpar filtros
                </Button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[56rem] border-collapse text-sm">
              <caption className="sr-only">Leads do funil com oferta, valor, último toque, estado e datas, ordenados por {ORDENACAO_LABEL[ordem].toLowerCase()}</caption>
              <thead>
                <tr className="border-b border-line text-left text-xs text-ink-subtle">
                  <th scope="col" className="px-5 py-3 font-medium">Lead</th>
                  <th scope="col" className="px-4 py-3 font-medium">{v.colunaOferta}</th>
                  <th scope="col" data-numeric="true" className="px-4 py-3 text-right font-medium" aria-sort={ordem === 'maior_valor' ? 'descending' : ordem === 'menor_valor' ? 'ascending' : 'none'}>
                    Valor
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">Último toque</th>
                  <th scope="col" className="px-4 py-3 font-medium">Estado</th>
                  <th scope="col" className="px-4 py-3 font-medium" aria-sort={ordem === 'recentes' ? 'descending' : ordem === 'antigos' ? 'ascending' : 'none'}>
                    {v.colunaEntrou}
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">{v.colunaVoltou}</th>
                </tr>
              </thead>
              <tbody>
                {pagina.map((lead) => (
                  <tr key={lead.id} className="border-b border-line/60 last:border-0 hover:bg-ink/5">
                    <th scope="row" className="px-5 py-3 text-left font-normal">
                      <span className="block text-ink">{shortenName(lead.name)}</span>
                      <span className="block text-xs text-ink-subtle">{maskPhoneDisplay(lead.phoneE164)}</span>
                    </th>
                    <td className="px-4 py-3 text-ink-muted">
                      {lead.offerName}
                      <span className="block text-xs text-ink-subtle">{PROVIDER_LABEL[lead.provider]}</span>
                    </td>
                    <td data-numeric="true" className="px-4 py-3 text-right tabular-nums text-ink">
                      {formatBRLFromCents(lead.amountCents)}
                    </td>
                    <td className="px-4 py-3">
                      <UltimoToque kind={kind} lead={lead} touches={touches} />
                    </td>
                    <td className="px-4 py-3">
                      <Estado lead={lead} />
                    </td>
                    <td className="px-4 py-3 tabular-nums text-ink-muted">{formatDateTime(lead.enteredAt)}</td>
                    <td className="px-4 py-3 text-ink-muted">
                      <Voltou lead={lead} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="flex flex-col gap-3 px-4 md:hidden">
            {pagina.map((lead) => (
              <li key={lead.id} className="flex flex-col gap-2 rounded-xl border border-line bg-surface px-4 py-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-ink">{shortenName(lead.name)}</p>
                    <p className="truncate text-xs text-ink-subtle">{maskPhoneDisplay(lead.phoneE164)}</p>
                  </div>
                  <p className="shrink-0 tabular-nums font-semibold text-ink">{formatBRLFromCents(lead.amountCents)}</p>
                </div>
                <p className="text-sm text-ink-muted">
                  {lead.offerName} <span className="text-ink-subtle">· {PROVIDER_LABEL[lead.provider]}</span>
                </p>
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line pt-2">
                  <Estado lead={lead} />
                  <UltimoToque kind={kind} lead={lead} touches={touches} />
                </div>
                <dl className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <dt className="text-ink-subtle">{v.colunaEntrou}</dt>
                    <dd className="tabular-nums text-ink">{formatDateTime(lead.enteredAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-ink-subtle">{v.colunaVoltou}</dt>
                    <dd>
                      <Voltou lead={lead} />
                    </dd>
                  </div>
                </dl>
              </li>
            ))}
          </ul>

          <div className="px-5 py-3">
            <Pagination total={ordenados.length} limit={POR_PAGINA} offset={inicio} onChange={setOffset} />
          </div>
        </>
      )}
    </SectionCard>
  );
}
